import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Rocket, FileSignature, Users, ArrowRight, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../contexts/ConfirmContext';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/api';
import DataTable from './ui/DataTable';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';
import { formatDate, normalizeApiError } from '../lib/i18n';
import { TimezoneSelector } from './ui/TimezoneSelector';
import { useTour } from './ui/TourManager';
import { campaignsTourSteps } from './ui/tourSteps';

// datetime-local string'ini (seçilen timezone'a göre) UTC ISO string'e çevirir
function localDatetimeToUtc(datetimeLocal, timezone) {
  if (!datetimeLocal) return undefined;
  const [datePart, timePart] = datetimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  // Seçilen timezone'a göre UTC offset'i bul
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
  const parts = tzFormatter.formatToParts(utcDate);
  const tzYear = parseInt(parts.find(p => p.type === 'year').value);
  const tzMonth = parseInt(parts.find(p => p.type === 'month').value);
  const tzDay = parseInt(parts.find(p => p.type === 'day').value);
  const tzHour = parseInt(parts.find(p => p.type === 'hour').value);
  const tzMinute = parseInt(parts.find(p => p.type === 'minute').value);
  const desiredLocalMs = new Date(Date.UTC(year, month - 1, day, hour, minute, 0)).getTime();
  const actualLocalMs = new Date(Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0)).getTime();
  const offsetMs = desiredLocalMs - actualLocalMs;
  const correctUtcMs = utcDate.getTime() + offsetMs;
  return new Date(correctUtcMs).toISOString();
}

export default function Campaigns() {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Contacts prefilled from CRM list "Add to Campaign" action
  const [prefillContacts, setPrefillContacts] = useState(location.state?.prefillContacts || []);

  // Pagination & Filtering
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [groupIds, setGroupIds] = useState([]);
  const [listIds, setListIds] = useState([]);
  const [mailAccountId, setMailAccountId] = useState('');
  const [senderPoolId, setSenderPoolId] = useState('');
  
  // Scheduling State
  const [startMode, setStartMode] = useState('IMMEDIATE');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [sendingWindowStart, setSendingWindowStart] = useState('09:00');
  const [sendingWindowEnd, setSendingWindowEnd] = useState('17:30');
  const [minDelaySec, setMinDelaySec] = useState(60);
  const [maxDelaySec, setMaxDelaySec] = useState(180);
  const [perDayLimit, setPerDayLimit] = useState(0);
  const [dispatchSortPolicy, setDispatchSortPolicy] = useState('COMPANY_SEQUENTIAL');
  const [openTracking, setOpenTracking] = useState(true);
  const [clickTracking, setClickTracking] = useState(true);
  
  const [activeTab, setActiveTab] = useState('general');

  // Selections
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lists, setLists] = useState([]);
  const [mailAccounts, setMailAccounts] = useState([]);
  const [senderPools, setSenderPools] = useState([]);
  const [isPoolBuilderOpen, setIsPoolBuilderOpen] = useState(false);
  const [newPoolName, setNewPoolName] = useState('');
  const [newPoolAccountIds, setNewPoolAccountIds] = useState([]);
  const [newPoolRotationStrategy, setNewPoolRotationStrategy] = useState('ROUND_ROBIN');
  const [isSavingPool, setIsSavingPool] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/campaigns', {
        params: {
          page,
          limit,
          search: searchTerm,
          status: statusFilter,
          sortBy,
          sortOrder
        }
      });
      setCampaigns(res.data.data || []);
      setTotalPages(res.data.pagination?.pages || 1);
      setTotalRecords(res.data.pagination?.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchTerm, statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.state?.dashboardStatusFilter) {
      setStatusFilter(location.state.dashboardStatusFilter);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchDependencies();
  }, []);

  const { startTour } = useTour();

  // Auto-start tour on first visit
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tourShown = localStorage.getItem('campaignsTourShown');
    if (tourShown !== 'true') {
      startTour('campaigns', campaignsTourSteps);
    }
  }, [startTour]);

  // Auto-open create modal if navigated from CRM list with prefilled contacts
// eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (location.state?.prefillContacts?.length > 0) {
      openCreateModal();
      // Clear the navigation state to avoid re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const fetchDependencies = async () => {
    try {
      // Pass a high limit for dropdowns as we need all active items
      const [tplRes, grpRes, listRes, mailRes, poolRes] = await Promise.all([
        api.get('/api/templates', { params: { limit: 1000, activeOnly: 'true' } }),
        api.get('/api/groups', { params: { limit: 1000 } }),
        api.get('/api/prospect-lists', { params: { limit: 1000 } }),
        api.get('/api/mail-accounts'),
        api.get('/api/sender-pools')
      ]);
      setTemplates(tplRes.data?.data || tplRes.data || []);
      setGroups(grpRes.data?.data || grpRes.data || []);
      setLists(listRes.data?.data || listRes.data || []);
      setMailAccounts(mailRes.data?.accounts || []);
      setSenderPools(poolRes.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setSelectedCampaign(null);
    setName('');
    setTemplateId('');
    setGroupIds([]);
    setListIds([]);
    setMailAccountId('');
    setSenderPoolId('');
    
    setStartMode('IMMEDIATE');
    setStartAt('');
    setEndAt('');
    // Initialize with browser timezone or fall back to UTC
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    setTimezone(browserTz);
    setWorkingDays([1, 2, 3, 4, 5]);
    setSendingWindowStart('09:00');
    setSendingWindowEnd('17:30');
    setMinDelaySec(60);
    setMaxDelaySec(180);
    setPerDayLimit(0);
    setOpenTracking(true);
    setClickTracking(true);
    setActiveTab('general');
    setIsPoolBuilderOpen(false);
    setNewPoolName('Campaign Sender Pool');
    setNewPoolAccountIds([]);
    setNewPoolRotationStrategy('ROUND_ROBIN');
    
    setIsModalOpen(true);
  };

  const openEditModal = (camp) => {
    setIsEditing(true);
    setSelectedCampaign(camp);
    setName(camp.name);
    setTemplateId(camp.templateId?._id || '');
    setGroupIds(camp.groupIds?.map(g => g._id) || []);
    setListIds(camp.listIds?.map(l => l._id) || []);
    setMailAccountId(camp.mailAccountId?._id || camp.mailAccountId || '');
    setSenderPoolId(camp.senderPoolId?._id || camp.senderPoolId || '');
    
    setStartMode(camp.startMode || 'IMMEDIATE');
    setStartAt(camp.startAt ? new Date(camp.startAt).toISOString().slice(0,16) : '');
    setEndAt(camp.endAt ? new Date(camp.endAt).toISOString().slice(0,16) : '');
    setTimezone(camp.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    setWorkingDays(camp.workingDays || [1, 2, 3, 4, 5]);
    setSendingWindowStart(camp.sendingWindowStart || '09:00');
    setSendingWindowEnd(camp.sendingWindowEnd || '17:30');
    setMinDelaySec(camp.minDelaySec !== undefined ? camp.minDelaySec : 60);
    setMaxDelaySec(camp.maxDelaySec !== undefined ? camp.maxDelaySec : 180);
    setPerDayLimit(camp.perDayLimit !== undefined ? camp.perDayLimit : 0);
    setOpenTracking(camp.tracking?.openTracking !== false);
    setClickTracking(camp.tracking?.clickTracking !== false);
    setActiveTab('general');
    setIsPoolBuilderOpen(false);
    setNewPoolName(`${camp.name} Sender Pool`);
    setNewPoolAccountIds(camp.senderPoolId?.mailAccountIds?.map(acc => acc._id || acc) || []);
    setNewPoolRotationStrategy(camp.senderPoolId?.rotationStrategy || 'ROUND_ROBIN');
    
    setIsModalOpen(true);
  };

  const toggleGroup = (id) => {
    setGroupIds(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const toggleList = (id) => {
    setListIds(prev => prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]);
  };

  const toggleNewPoolAccount = (id) => {
    setNewPoolAccountIds(prev => prev.includes(id) ? prev.filter(accountId => accountId !== id) : [...prev, id]);
  };

  const resolveAccountLabel = (account) => account?.fromName || account?.from || account?.user || 'Unnamed Account';

  const handleCreateSenderPoolInline = async () => {
    if (!newPoolName.trim()) {
      toast.warning(t('errors.validation'));
      return;
    }
    if (newPoolAccountIds.length === 0) {
      toast.warning(t('errors.validation'));
      return;
    }

    try {
      setIsSavingPool(true);
      const res = await api.post('/api/sender-pools', {
        name: newPoolName.trim(),
        mailAccountIds: newPoolAccountIds,
        rotationStrategy: newPoolRotationStrategy,
        isActive: true
      });

      const createdPool = res.data?.data;
      if (!createdPool?._id) {
        throw new Error('Sender pool creation failed');
      }

      setSenderPools(prev => [createdPool, ...prev]);
      setSenderPoolId(createdPool._id);
      setMailAccountId('');
      setIsPoolBuilderOpen(false);
      toast.success(t('actions.createPool'));
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'errors.generic'));
    } finally {
      setIsSavingPool(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Campaign name is required');
      return;
    }

    if (!mailAccountId && !senderPoolId) {
      toast.error('Please select either a Mail Account or a Sender Pool');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
          name,
          templateId: templateId || undefined,
          groupIds,
          listIds,
          mailAccountId: mailAccountId || undefined,
          senderPoolId: senderPoolId || undefined,
          startMode, 
          startAt: startAt ? localDatetimeToUtc(startAt, timezone) : undefined,
          endAt: endAt ? localDatetimeToUtc(endAt, timezone) : undefined,
          timezone, workingDays, sendingWindowStart, sendingWindowEnd,
          minDelaySec: parseInt(minDelaySec, 10),
          maxDelaySec: parseInt(maxDelaySec, 10),
          perDayLimit: parseInt(perDayLimit, 10),
          dispatchSortPolicy,
          tracking: {
            openTracking,
            clickTracking
          }
      };

      if (isEditing) {
        await api.put(`/api/campaigns/${selectedCampaign._id}`, payload);
      } else {
        await api.post('/api/campaigns', payload);
      }
      setIsModalOpen(false);
      fetchCampaigns();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'errors.saveCampaign'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm(t('campaigns.deleteConfirmTitle'), t('campaigns.deleteConfirmDescription'));
    if (!ok) return;
    try {
      const res = await api.delete(`/api/campaigns/${id}`);
      toast.success(res.data?.message || t('campaigns.removed'));
      fetchCampaigns();
    } catch (err) {
      toast.error(normalizeApiError(err, t, 'errors.deleteCampaign'));
    }
  };

  const getCampaignHealthMeta = (camp) => {
    if (!camp.templateId) return { label: t('campaigns.health.templateMissing'), tone: 'text-rose-600 bg-rose-500/10 border-rose-500/20' };
    if (!camp.mailAccountId && !camp.senderPoolId) return { label: t('campaigns.health.senderMissing'), tone: 'text-amber-600 bg-amber-500/10 border-amber-500/20' };
    if (!camp.groupIds?.length && !camp.listIds?.length) return { label: t('campaigns.health.audienceMissing'), tone: 'text-amber-600 bg-amber-500/10 border-amber-500/20' };
    if (camp.status === 'DRAFT' || camp.status === 'SCHEDULED') return { label: t('campaigns.health.launchReady'), tone: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20' };
    if (camp.status === 'PAUSED') return { label: t('campaigns.health.needsResume'), tone: 'text-sky-600 bg-sky-500/10 border-sky-500/20' };
    return { label: t('campaigns.health.monitoring'), tone: 'text-muted-foreground bg-muted border-border' };
  };

  const columns = [
    {
      header: t('campaigns.columns.name'),
      accessor: 'name',
      sortable: true,
      render: (camp) => (
        <div 
          className="cursor-pointer group/name" 
          onClick={() => navigate(`/campaigns/${camp._id}`)}
        >
          <div className="font-bold tracking-tight text-foreground text-sm group-hover/name:text-primary group-hover/name:underline transition-all decoration-2 underline-offset-4 decoration-primary/30">{camp.name}</div>
          <div className="text-[11px] font-semibold text-muted-foreground mt-0.5">{formatDate(camp.createdAt, {}, i18n.language)}</div>
          <div className="mt-2">
            <span className={`inline-flex items-center rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-widest ${getCampaignHealthMeta(camp).tone}`}>
              {getCampaignHealthMeta(camp).label}
            </span>
          </div>
        </div>
      )
    },
    {
      header: t('campaigns.columns.status'),
      accessor: 'status',
      sortable: true,
      render: (camp) => (
        <StatusBadge 
          variant={
            camp.status === 'RUNNING' ? 'success' : 
            camp.status === 'PAUSED' ? 'warning' : 
            camp.status === 'DRAFT' ? 'indigo' : 
            camp.status === 'ARCHIVED' ? 'default' : 
            'default'
          }
          dot={camp.status === 'RUNNING'}
        >
          {camp.status || 'UNKNOWN'}
        </StatusBadge>
      )
    },
    {
      header: t('campaigns.columns.template'),
      accessor: 'templateId',
      sortable: false,
      render: (camp) => camp.templateId ? (
        <div className="flex items-center text-sm font-semibold text-foreground">
          <FileSignature className="w-4 h-4 mr-2 text-primary/70" />
          {camp.templateId.name}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground italic font-medium">{t('campaigns.noTemplate')}</span>
      )
    },
    {
      header: t('campaigns.columns.groups'),
      accessor: 'groupIds',
      sortable: false,
      render: (camp) => (camp.groupIds?.length > 0 || camp.listIds?.length > 0) ? (
        <div className="flex flex-wrap gap-1.5">
          {camp.groupIds?.map(g => (
            <span key={g._id} className="text-[11px] font-bold bg-primary/10 text-primary border border-primary/20 px-2.5 py-1 rounded-lg flex items-center shadow-sm">
              <Users className="w-3.5 h-3.5 mr-1" />
              {g.name}
            </span>
          ))}
          {camp.listIds?.map(l => (
            <span key={l._id} className="text-[11px] font-bold bg-violet-500/10 text-violet-600 border border-violet-500/20 px-2.5 py-1 rounded-lg flex items-center shadow-sm">
              <ClipboardList className="w-3.5 h-3.5 mr-1" />
              {l.name}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground italic font-medium">{t('campaigns.noGroups')}</span>
      )
    },
    {
      header: t('campaigns.columns.createdDate'),
      accessor: 'createdAt',
      sortable: true,
      align: 'right',
      render: (camp) => (
        <div className="flex flex-col items-end justify-center w-full h-full gap-2 relative">
          <div className="text-[11px] font-bold text-muted-foreground">{formatDate(camp.createdAt, {}, i18n.language)}</div>
          <div className="flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
            <button onClick={() => openEditModal(camp)} className="text-muted-foreground hover:bg-muted hover:text-foreground p-1.5 rounded-lg transition-all shadow-sm"><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={() => handleDelete(camp._id)} className="text-[10px] font-black uppercase tracking-widest text-destructive hover:bg-destructive/10 px-2.5 py-1.5 rounded-lg transition-all shadow-sm">Delete</button>
            <button
              onClick={() => navigate(`/campaigns/${camp._id}`)}
              className="flex items-center text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 hover:bg-primary hover:text-white px-3 py-1.5 rounded-lg transition-all"
            >
              View <ArrowRight className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>
      )
    }
  ];

  if (loading && campaigns.length === 0) return <div className="p-8 flex justify-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div></div>;

  const campaignFilterOptions = [
    { label: t('campaigns.filters.allStates'), value: 'All' },
    { label: t('status.draft'), value: 'DRAFT', icon: <span className="w-2 h-2 rounded-full bg-muted-foreground mr-2"></span> },
    { label: t('status.running'), value: 'RUNNING', icon: <span className="w-2 h-2 rounded-full bg-success mr-2"></span> },
    { label: t('status.paused'), value: 'PAUSED', icon: <span className="w-2 h-2 rounded-full bg-warning mr-2"></span> },
    { label: t('common.archived'), value: 'ARCHIVED', icon: <span className="w-2 h-2 rounded-full bg-muted-foreground opacity-50 mr-2"></span> }
  ];

  return (
    <PageShell>
      <PageHeader
        title={t('campaigns.title')}
        subtitle={t('campaigns.subtitle')}
        icon={Rocket}
        className="campaigns-header"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={() => startTour('campaigns', campaignsTourSteps)}
              className="border-primary/30 text-primary hover:bg-primary/5 px-4 py-3 rounded-xl shadow-sm text-sm font-bold flex items-center transition-all border"
              title="Start Tour"
            >
              <Rocket className="w-4 h-4 mr-2" />
              Tour
            </button>
            <button
              onClick={openCreateModal}
              className="create-campaign-btn bg-primary text-primary-foreground px-6 py-3 rounded-xl shadow-sm text-sm font-bold hover:bg-primary/90 flex items-center transition-all"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('campaigns.create')}
            </button>
          </div>
        }
      />

      {prefillContacts.length > 0 && (
        <div className="mb-6 bg-violet-500/10 border border-violet-500/20 text-violet-700 text-sm font-bold px-5 py-3.5 rounded-2xl flex items-center gap-3">
          <Users className="w-4 h-4 shrink-0" />
          <span>{t('campaigns.selectedContacts', { count: prefillContacts.length })}</span>
          <button onClick={() => setPrefillContacts([])} className="ml-auto text-violet-500 hover:text-violet-700 font-black text-xs uppercase tracking-widest">{t('actions.dismiss')}</button>
        </div>
      )}

      {error && <div className="bg-destructive/10 text-destructive text-sm font-medium p-4 rounded-xl border border-destructive/20 mb-8">{error}</div>}

      <div className="campaign-filters campaigns-table bg-card border border-border rounded-[2.5rem] shadow-sm overflow-hidden">
        <DataTable
        columns={columns}
        data={campaigns}
        loading={loading}
        pagination={{ page, limit, totalRecords, totalPages }}
        onPageChange={setPage}
        onLimitChange={(v) => { setLimit(v); setPage(1); }}
        sort={{ sortBy, sortOrder }}
        onSort={handleSort}
        search={{
          placeholder: t('campaigns.searchPlaceholder'),
          value: searchTerm,
          onChange: (val) => { setSearchTerm(val); setPage(1); }
        }}
        filter={{
          value: statusFilter,
          onChange: (val) => { setStatusFilter(val); setPage(1); },
          options: campaignFilterOptions
        }}
        emptyState={
          <EmptyState 
            title={t('campaigns.emptyTitle')}
            description={t('campaigns.emptyDescription')}
            icon={Rocket}
          />
        }
      />
      </div>

      {/* Editor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in zoom-in-95">
          <div className="bg-card rounded-[2.5rem] shadow-2xl w-full max-w-lg overflow-hidden border border-border/50 flex flex-col max-h-[90vh]">
            <div className="px-8 py-6 border-b border-border bg-card/50 backdrop-blur-md">
              <h2 className="text-2xl font-black tracking-tight text-foreground">
                {isEditing ? 'Modify Campaign Definition' : 'Define New Campaign'}
              </h2>
            </div>

            <div className="p-8 overflow-y-auto max-h-[70vh]">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-8 bg-muted/50 p-1 rounded-xl">
                  <TabsTrigger value="general" className="rounded-lg data-[state=active]:shadow-sm data-[state=active]:bg-background font-bold tracking-tight">Configuration</TabsTrigger>
                  <TabsTrigger value="rules" className="rounded-lg data-[state=active]:shadow-sm data-[state=active]:bg-background font-bold tracking-tight">Delivery Rules</TabsTrigger>
                </TabsList>
                
                <TabsContent value="general" className="space-y-6 mt-0">
              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Campaign Designation</label>
                <input
                  type="text"
                  value={name}
                  autoFocus
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Q3 Founders Outreach"
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all"
                />
              </div>

              <div className="space-y-4 bg-muted/20 p-4 rounded-3xl border border-border mb-6">
                <div className="flex gap-4">
                  <button 
                    type="button"
                    onClick={() => { setMailAccountId(''); setSenderPoolId(''); }}
                    className={cn(
                      "flex-1 px-4 py-3 rounded-2xl border-2 transition-all font-bold text-xs",
                      !senderPoolId ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground"
                    )}
                  >
                    Explicit Account
                  </button>
                  <button 
                    type="button"
                    onClick={() => { setMailAccountId(''); setSenderPoolId(''); setSenderPoolId(senderPools[0]?._id || ''); }}
                    className={cn(
                      "flex-1 px-4 py-3 rounded-2xl border-2 transition-all font-bold text-xs",
                      senderPoolId ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground"
                    )}
                  >
                    Sender Pool
                  </button>
                </div>

                {!senderPoolId ? (
                  <div>
                    <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 opacity-60">Select Account</label>
                    <Select value={mailAccountId} onValueChange={setMailAccountId}>
                      <SelectTrigger className="w-full bg-background border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:ring-primary focus:border-primary font-bold h-12 px-5 transition-all">
                        <SelectValue placeholder="-- Select Sender --" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                        {mailAccounts.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-xs text-muted-foreground font-medium mb-3">No mail accounts found.</p>
                            <button onClick={() => navigate('/mail-accounts')} className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline">Connect Account</button>
                          </div>
                        ) : (
                          mailAccounts.map(ma => (
                            <SelectItem key={ma._id} value={ma._id} className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                              {ma.fromName || ma.user} <span className="opacity-50 text-xs ml-1 font-medium italic">({ma.user})</span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest opacity-60">Select Pool</label>
                      <button
                        type="button"
                        onClick={() => {
                          setIsPoolBuilderOpen(prev => !prev);
                          setNewPoolName(`${name || 'Campaign'} Sender Pool`);
                          setNewPoolAccountIds([]);
                          setNewPoolRotationStrategy('ROUND_ROBIN');
                        }}
                        className="text-[10px] font-black uppercase tracking-widest text-primary hover:underline"
                      >
                        Create Pool
                      </button>
                    </div>
                    <Select value={senderPoolId} onValueChange={setSenderPoolId}>
                      <SelectTrigger className="w-full bg-background border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:ring-primary focus:border-primary font-bold h-12 px-5 transition-all">
                        <SelectValue placeholder="-- Select Sender Pool --" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                        {senderPools.length === 0 ? (
                          <div className="p-4 text-center">
                            <p className="text-xs text-muted-foreground font-medium mb-3">No pools found.</p>
                            <button onClick={() => navigate('/sender-pools')} className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline">Create Pool</button>
                          </div>
                        ) : (
                          senderPools.map(pool => (
                            <SelectItem key={pool._id} value={pool._id} className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                              {pool.name} <span className="opacity-50 text-xs ml-1 font-medium italic">({pool.mailAccountIds?.length || 0} senders)</span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {senderPoolId && (
                      <p className="mt-2 text-[10px] text-muted-foreground font-medium">
                        {(senderPools.find(pool => pool._id === senderPoolId)?.rotationStrategy || 'ROUND_ROBIN').replace(/_/g, ' ')} rotation across {senderPools.find(pool => pool._id === senderPoolId)?.mailAccountIds?.length || 0} sender(s).
                      </p>
                    )}
                    {isPoolBuilderOpen && (
                      <div className="mt-4 space-y-4 rounded-3xl border border-primary/15 bg-primary/5 p-4">
                        <div>
                          <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 opacity-60">Pool Name</label>
                          <input
                            type="text"
                            value={newPoolName}
                            onChange={e => setNewPoolName(e.target.value)}
                            className="w-full bg-background border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3 px-4 transition-all text-sm"
                            placeholder="Campaign Sender Pool"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 opacity-60">Rotation Strategy</label>
                          <Select value={newPoolRotationStrategy} onValueChange={setNewPoolRotationStrategy}>
                            <SelectTrigger className="w-full bg-background border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:ring-primary focus:border-primary font-bold h-12 px-5 transition-all">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                              <SelectItem value="ROUND_ROBIN">Round Robin</SelectItem>
                              <SelectItem value="WEIGHTED">Weighted</SelectItem>
                              <SelectItem value="RANDOM">Random</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 opacity-60">Included Accounts</label>
                          <div className="max-h-44 overflow-y-auto rounded-2xl border border-border/50 bg-background p-2 space-y-2">
                            {mailAccounts.length === 0 ? (
                              <div className="p-4 text-center text-xs text-muted-foreground font-medium">Connect mail accounts first.</div>
                            ) : (
                              mailAccounts.map(account => {
                                const selected = newPoolAccountIds.includes(account._id);
                                return (
                                  <button
                                    key={account._id}
                                    type="button"
                                    onClick={() => toggleNewPoolAccount(account._id)}
                                    className={cn(
                                      "w-full rounded-xl border px-3 py-2 text-left transition-all flex items-center justify-between",
                                      selected ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"
                                    )}
                                  >
                                    <span className="text-sm font-bold">{resolveAccountLabel(account)}</span>
                                    <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{selected ? 'Selected' : 'Add'}</span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-[10px] text-muted-foreground font-medium">
                            This creates the pool and immediately assigns it to the campaign.
                          </p>
                          <button
                            type="button"
                            onClick={handleCreateSenderPoolInline}
                            disabled={isSavingPool || !newPoolName.trim() || newPoolAccountIds.length === 0}
                            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                          >
                            {isSavingPool ? 'Creating...' : 'Save Pool'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Creative Asset (Template)</label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:ring-primary focus:border-primary font-bold h-14 px-5 transition-all">
                    <SelectValue placeholder="-- Select a Template --" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                    {templates.length === 0 ? (
                      <div className="p-4 text-center">
                        <p className="text-xs text-muted-foreground font-medium mb-3">No templates found.</p>
                        <button onClick={() => navigate('/templates')} className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline">Create Template</button>
                      </div>
                    ) : (
                      templates.map(t => (
                        <SelectItem key={t._id} value={t._id} className="font-bold cursor-pointer hover:bg-muted focus:bg-primary/10 transition-colors py-3">
                          {t.name} <span className="opacity-50 text-xs ml-1 font-medium italic">(Subj: {t.subject})</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Audience Segmentation</label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Groups */}
                  <div className="max-h-56 overflow-y-auto border-2 border-border/50 rounded-2xl shadow-inner bg-muted/10 p-3 space-y-2">
                    {groups.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-6 text-center font-medium border border-dashed border-border rounded-xl flex flex-col items-center justify-center">
                        <p className="mb-2">No segments found.</p>
                        <button onClick={() => navigate('/groups')} className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline mt-2">Build a Target Group</button>
                      </div>
                    ) : (
                      groups.map(g => {
                        const isSelected = groupIds.includes(g._id);
                        return (
                          <div
                            key={g._id}
                            onClick={() => toggleGroup(g._id)}
                            className={`p-3 rounded-xl flex items-center cursor-pointer border-2 transition-all duration-300 group ${isSelected ? 'bg-primary/5 border-primary shadow-sm hover:border-primary/80 transform scale-[1.01]' : 'bg-background border-border hover:border-primary/40 hover:shadow-md'}`}
                          >
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 bg-background group-hover:border-primary/50'}`}>
                              {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div className="flex-1 truncate pr-2">
                              <p className={`text-sm font-bold tracking-tight truncate ${isSelected ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>{g.name}</p>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase truncate mt-0.5">{g.memberCount} active personnel</p>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>

                  {/* Lists */}
                  <div className="max-h-56 overflow-y-auto border-2 border-border/50 rounded-2xl shadow-inner bg-muted/10 p-3 space-y-2">
                    {lists.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-6 text-center font-medium border border-dashed border-border rounded-xl flex flex-col items-center justify-center">
                        <p className="mb-2">No lists found.</p>
                        <button onClick={() => navigate('/crm/lists')} className="text-[11px] font-black uppercase tracking-widest text-primary hover:underline mt-2">Build a List</button>
                      </div>
                    ) : (
                      lists.map(l => {
                        const isSelected = listIds.includes(l._id);
                        return (
                          <div
                            key={l._id}
                            onClick={() => toggleList(l._id)}
                            className={`p-3 rounded-xl flex items-center cursor-pointer border-2 transition-all duration-300 group ${isSelected ? 'bg-violet-500/5 border-violet-500 shadow-sm hover:border-violet-500/80 transform scale-[1.01]' : 'bg-background border-border hover:border-violet-500/40 hover:shadow-md'}`}
                          >
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-violet-500 border-violet-500' : 'border-muted-foreground/30 bg-background group-hover:border-violet-500/50'}`}>
                              {isSelected && <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <div className="flex-1 truncate pr-2">
                              <p className={`text-sm font-bold tracking-tight truncate ${isSelected ? 'text-violet-600' : 'text-foreground group-hover:text-violet-600'}`}>{l.name}</p>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase truncate mt-0.5">{l.prospectCount || 0} leads</p>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>
                
                <TabsContent value="rules" className="space-y-6 mt-0 animate-in fade-in slide-in-from-bottom-2">
                  <div className="grid grid-cols-2 gap-4 bg-muted/20 p-4 rounded-3xl border border-border">
                    <div
                      className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${startMode === 'IMMEDIATE' ? 'border-primary bg-primary/5 text-primary shadow-sm' : 'border-border/50 bg-background text-muted-foreground hover:border-primary/50'}`}
                      onClick={() => setStartMode('IMMEDIATE')}
                    >
                      <h4 className="font-black tracking-tight mb-1">Start Now</h4>
                      <p className="text-[10px] leading-tight font-medium opacity-80">Launch as soon as configured.</p>
                    </div>
                    <div
                      className={`cursor-pointer rounded-2xl p-4 border-2 transition-all ${startMode === 'SCHEDULED' ? 'border-primary bg-primary/5 text-primary shadow-sm' : 'border-border/50 bg-background text-muted-foreground hover:border-primary/50'}`}
                      onClick={() => setStartMode('SCHEDULED')}
                    >
                      <h4 className="font-black tracking-tight mb-1">Schedule</h4>
                      <p className="text-[10px] leading-tight font-medium opacity-80">Set a specific time to begin.</p>
                    </div>
                  </div>

                  {startMode === 'SCHEDULED' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                         <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Start At</label>
                         <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                      </div>
                      <div>
                         <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">End At (Optional)</label>
                         <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                      </div>
                    </div>
                  )}

                  <div>
                     <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Sending Timezone</label>
                     <TimezoneSelector value={timezone} onChange={setTimezone} />
                  </div>


                  <div>
                     <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Working Days</label>
                     <div className="flex flex-wrap gap-2">
                       {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, idx) => (
                           <button 
                             key={day} 
                             onClick={() => setWorkingDays(prev => prev.includes(idx) ? prev.filter(d => d !== idx) : [...prev, idx].sort())}
                             className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${workingDays.includes(idx) ? 'bg-primary text-white shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                           >
                              {day}
                           </button>
                       ))}
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                     <div>
                       <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Window Start</label>
                       <input type="time" value={sendingWindowStart} onChange={e => setSendingWindowStart(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                     </div>
                     <div>
                       <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Window End</label>
                       <input type="time" value={sendingWindowEnd} onChange={e => setSendingWindowEnd(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                     </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                     <div>
                       <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Min Delay (s)</label>
                       <input type="number" min="0" value={minDelaySec} onChange={e => setMinDelaySec(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                     </div>
                     <div>
                       <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Max Delay (s)</label>
                       <input type="number" min="0" value={maxDelaySec} onChange={e => setMaxDelaySec(e.target.value)} className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                     </div>
                     <div>
                        <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Daily Limit</label>
                        <input type="number" min="0" value={perDayLimit} onChange={e => setPerDayLimit(e.target.value)} placeholder="0 = No Limit" className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm" />
                      </div>
                   </div>
                   <div className="mt-4">
                     <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-2.5 opacity-60">Gönderim Sıralaması / Order</label>
                     <select
                       value={dispatchSortPolicy}
                       onChange={e => setDispatchSortPolicy(e.target.value)}
                       className="w-full bg-muted/30 border-2 border-border/50 text-foreground shadow-sm rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all text-sm"
                     >
                       <option value="COMPANY_SEQUENTIAL">🏢 Şirket Bazlı Sıralı (Bir Şirket Bitince Diğerine)</option>
                       <option value="COMPANY_INTERLEAVED">🔄 Şirketler Arası Dönüşümlü (Spam Korumalı)</option>
                       <option value="SENIORITY_FIRST">👑 Kıdem / Unvan Öncelikli (CEO/VP İlk Sırada)</option>
                       <option value="DEFAULT">⏱️ Eklenme Sırasına Göre</option>
                     </select>
                   </div>
                  <p className="text-[10px] text-muted-foreground font-medium italic mt-2 opacity-80">* Daily limit of 0 means unlimited within the window.</p>

                  <div className="border-t border-border/50 pt-4 space-y-3">
                    <label className="block text-[11px] font-black text-muted-foreground uppercase tracking-widest mb-1.5 opacity-60">Engagement Tracking</label>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-start space-x-3 bg-muted/20 hover:bg-muted/40 p-4 rounded-2xl border border-border/50 cursor-pointer select-none transition-all">
                        <input
                          type="checkbox"
                          checked={openTracking}
                          onChange={e => setOpenTracking(e.target.checked)}
                          className="w-4 h-4 mt-0.5 rounded text-primary focus:ring-primary border-border"
                        />
                        <div>
                          <span className="text-xs font-black text-foreground block">Email Open Tracking</span>
                          <span className="text-[10px] text-muted-foreground block leading-tight mt-0.5">Injects a hidden tracking pixel to detect when emails are opened.</span>
                        </div>
                      </label>
                      <label className="flex items-start space-x-3 bg-muted/20 hover:bg-muted/40 p-4 rounded-2xl border border-border/50 cursor-pointer select-none transition-all">
                        <input
                          type="checkbox"
                          checked={clickTracking}
                          onChange={e => setClickTracking(e.target.checked)}
                          className="w-4 h-4 mt-0.5 rounded text-primary focus:ring-primary border-border"
                        />
                        <div>
                          <span className="text-xs font-black text-foreground block">Link Click Tracking</span>
                          <span className="text-[10px] text-muted-foreground block leading-tight mt-0.5">Rewrites external links in emails to track click metrics.</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                </TabsContent>
              </Tabs>
            </div>

            <div className="px-8 py-6 border-t border-border bg-muted/10 flex justify-end gap-4 shrink-0">
              <button onClick={() => setIsModalOpen(false)} className="px-6 py-3 text-[11px] font-black uppercase tracking-widest text-muted-foreground transition-all hover:text-foreground">Abort</button>
              <button
                onClick={handleSave}
                disabled={isSubmitting}
                className="px-8 py-3 text-[11px] font-black uppercase tracking-widest text-white bg-primary rounded-xl shadow-lg transition-all active:scale-[0.98] hover:scale-[1.02] disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Confirm Setup'}
              </button>
            </div>
          </div>
        </div>
      )}
      

    </PageShell>
  );
}
